import * as React from 'react'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { DayPicker, getDefaultClassNames, useDayPicker } from 'react-day-picker'
import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ChevronIcon } from '@/components/icons'
import { format } from 'date-fns'

function CustomMonthNavigation({ month: displayMonth, className, children }) {
    const { goToMonth, nextMonth, previousMonth, months: displayedMonths } = useDayPicker()
    const currentDate = new Date(children)
    const currentYear = currentDate.getFullYear()

    // For the displayed month - if this is being called for the second month in a multi-month display,
    // just show the month/year text without dropdowns
    const currentDisplayMonth = displayMonth || new Date()

    // Always use the first month for navigation controls
    const navigationMonth =
        displayedMonths && displayedMonths.length > 0 && displayedMonths[0].date
            ? displayedMonths[0].date
            : currentDisplayMonth

    const monthNames = Array.from({ length: 12 }, (_, i) => format(new Date(2000, i, 1), 'MMMM'))

    const maxYear = new Date().getFullYear() // Current year as maximum
    let years = Array.from({ length: 10 }, (_, i) => maxYear - 9 + i)

    if (!years.includes(currentYear)) {
        years.push(currentYear)
        years = years.sort()
    }

    const handleMonthChange = (value) => {
        const newMonth = new Date(navigationMonth)
        newMonth.setMonth(parseInt(value))
        goToMonth(newMonth)
    }

    const handleYearChange = (value) => {
        const newMonth = new Date(navigationMonth)
        newMonth.setFullYear(parseInt(value))
        goToMonth(newMonth)
    }

    const handlePreviousMonth = () => {
        if (previousMonth) goToMonth(previousMonth)
    }

    const handleNextMonth = () => {
        if (nextMonth) goToMonth(nextMonth)
    }

    return (
        <div className={cn('tw:flex tw:items-center tw:justify-between tw:w-full tw:gap-4', className)}>
            <style jsx>{`
                .rdp-week:has(.rdp-range_middle[data-hidden='true'])
                    .rdp-range_middle:not([data-hidden='true']):not(
                        :has(~ .rdp-range_middle:not([data-hidden='true']))
                    ) {
                    border-radius: 8px;
                }

                .rdp-week td[data-hidden='true'] + td:not([data-hidden='true']) {
                    border-top-left-radius: 8px !important;
                    border-bottom-left-radius: 8px !important;
                }

                .rdp-week td[data-hidden='true'] + td:not([data-hidden='true']) button {
                    border-top-left-radius: 8px !important;
                    border-bottom-left-radius: 8px !important;
                }
            `}</style>

            <div className="tw:flex tw:gap-4 tw:items-center">
                <Select value={currentDate.getMonth().toString()} onValueChange={handleMonthChange}>
                    <SelectTrigger className="tw:h-7 tw:w-30 tw:text-sm">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {monthNames.map((monthName, index) => (
                            <SelectItem key={index} value={index.toString()}>
                                {monthName}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={currentDate.getFullYear().toString()} onValueChange={handleYearChange}>
                    <SelectTrigger className="tw:h-7 tw:text-sm tw:w-20">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {years.map((year) => (
                            <SelectItem key={year} value={year.toString()}>
                                {year}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="tw:flex tw:gap-2">
                <Button
                    variant="secondary"
                    size="icon"
                    className="tw:h-8 tw:w-8"
                    onClick={handlePreviousMonth}
                    disabled={!previousMonth}
                >
                    <ChevronIcon className="tw:text-white tw:size-5 tw:rotate-90" />
                </Button>
                <Button
                    variant="secondary"
                    size="icon"
                    className="tw:h-8 tw:w-8"
                    onClick={handleNextMonth}
                    disabled={!nextMonth}
                >
                    <ChevronIcon className="tw:text-white tw:size-5 tw:rotate-270" />
                </Button>
            </div>
        </div>
    )
}

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    buttonVariant = 'ghost',
    weekStartsOn = 1,
    numberOfMonths = 1,
    formatters,
    components,
    ...props
}) {
    const defaultClassNames = getDefaultClassNames()

    return (
        <DayPicker
            showOutweekStartsOn={weekStartsOn}
            numberOfMonths={numberOfMonths}
            showOutsideDays={numberOfMonths === 1}
            sideDays={showOutsideDays}
            className={cn(
                'tw:!rounded-lg tw:bg-background tw:group/calendar tw:[--cell-size:--spacing(8)]',
                'tw:[[data-slot=card-content]_&]:bg-transparent tw:[[data-slot=popover-content]_&]:bg-transparent',
                String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
                String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
                className,
            )}
            captionLayout={captionLayout}
            formatters={{
                formatMonthDropdown: (date) => date.toLocaleString('default', { month: 'short' }),
                ...formatters,
            }}
            classNames={{
                root: cn('tw:w-fit tw:pr-3', defaultClassNames.root),
                months: cn(
                    'tw:flex tw:gap-0 tw:flex-col tw:md:flex-row tw:relative tw:divide-y tw:md:divide-y-0 tw:md:divide-x tw:divide-gray-300 tw:dark:divide-gray-600',
                    defaultClassNames.months,
                ),
                month: cn(
                    'tw:flex tw:flex-col tw:w-full tw:gap-4 tw:px-3 tw:py-3 tw:first:pl-0 tw:last:pr-0',
                    defaultClassNames.month,
                ),
                nav: cn('tw:hidden', defaultClassNames.nav),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'tw:size-(--cell-size) tw:aria-disabled:opacity-50 tw:p-0 tw:select-none',
                    defaultClassNames.button_previous,
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'tw:size-(--cell-size) tw:aria-disabled:opacity-50 tw:p-0 tw:select-none',
                    defaultClassNames.button_next,
                ),
                month_caption: cn(
                    'tw:flex tw:items-center tw:justify-center tw:h-(--cell-size) tw:w-full tw:px-(--cell-size) tw:p-0',
                    defaultClassNames.month_caption,
                ),
                dropdowns: cn(
                    'tw:w-full tw:flex tw:items-center tw:text-sm tw:font-medium tw:justify-center tw:h-(--cell-size) tw:gap-1.5',
                    defaultClassNames.dropdowns,
                ),
                dropdown_root: cn(
                    'tw:relative tw:has-focus:border-ring tw:border tw:border-input tw:shadow-xs tw:has-focus:ring-ring/50 tw:has-focus:ring-[3px] tw:rounded-md',
                    defaultClassNames.dropdown_root,
                ),
                dropdown: cn('tw:absolute tw:bg-popover tw:inset-0 tw:opacity-0', defaultClassNames.dropdown),
                caption_label: cn(
                    'tw:select-none tw:font-medium',
                    captionLayout === 'label'
                        ? 'tw:text-sm'
                        : 'tw:rounded-lg tw:pl-2 tw:pr-1 tw:flex tw:items-center tw:gap-1 tw:text-sm tw:h-8 tw:[&>svg]:text-muted-foreground tw:[&>svg]:size-3.5',
                    defaultClassNames.caption_label,
                ),
                table: 'tw:w-full tw:border-collapse',
                weekdays: cn('tw:flex tw:text-center', defaultClassNames.weekdays),
                weekday: cn(
                    'tw:!text-muted-foreground tw:rounded-lg tw:flex-1 tw:font-normal tw:text-[0.8rem] tw:select-none',
                    'tw:size-10 tw:leading-10',
                    defaultClassNames.weekday,
                ),
                week: cn('tw:flex tw:w-full tw:mt-2 ', defaultClassNames.week),
                week_number_header: cn('tw:select-none tw:w-(--cell-size)', defaultClassNames.week_number_header),
                week_number: cn(
                    'tw:text-[0.8rem] tw:select-none tw:text-muted-foreground',
                    defaultClassNames.week_number,
                ),
                day: cn(
                    'tw:relative tw:w-full tw:h-full tw:p-0 tw:text-center tw:[&:first-child]:!rounded-l-lg',
                    'tw:[&:last-child[data-selected=true]_button]:!rounded-r-lg tw:group/day tw:aspect-square tw:select-none',
                    'tw:[&:first-child[data-selected=true]_button]:!rounded-lg',
                    'tw:[&:last-child]:rounded-r-lg',
                    // 'tw:[&:last-child:not([data-hidden=true])]:rounded-r-lg',
                    // 'tw:[&:last-child:not([data-hidden=true])]:rounded-r-lg',
                    defaultClassNames.day,
                ),
                range_start: cn('tw:!rounded-l-md tw:bg-[#102855]', defaultClassNames.range_start),
                range_middle: cn('tw:rounded-none tw:bg-[#102855]', defaultClassNames.range_middle),
                range_end: cn('tw:!rounded-r-lg tw:bg-[#102855]', defaultClassNames.range_end),
                today: cn('', defaultClassNames.today),
                outside: cn(
                    'tw:!text-muted-foreground tw:aria-selected:text-muted-foreground',
                    defaultClassNames.outside,
                ),
                disabled: cn('tw:text-muted-foreground tw:opacity-50', defaultClassNames.disabled),
                hidden: cn('tw:invisible', defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                // Root: ({ className, rootRef, ...props }) => {
                //     return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />
                // },
                Chevron: ({ className, orientation, ...props }) => {
                    if (orientation === 'left') {
                        return <ChevronLeftIcon className={cn('tw:size-4 tw:min-w-4', className)} {...props} />
                    }
                    if (orientation === 'right') {
                        return (
                            <ChevronRightIcon
                                className={cn('tw:size-4 tw:min-w-4 tw:text-white', className)}
                                {...props}
                            />
                        )
                    }
                    return <ChevronDownIcon className={cn('tw:size-4 tw:min-w-4', className)} {...props} />
                },
                DayButton: CalendarDayButton,
                CaptionLabel: CustomMonthNavigation,
                WeekNumber: ({ children, ...props }) => {
                    return (
                        <td {...props}>
                            <div className="tw:flex tw:size-(--cell-size) tw:items-center tw:justify-center tw:text-center">
                                {children}
                            </div>
                        </td>
                    )
                },
                ...components,
            }}
            {...props}
        />
    )
}

function CalendarDayButton({ className, day, modifiers, ...props }) {
    const defaultClassNames = getDefaultClassNames()

    const ref = React.useRef(null)
    React.useEffect(() => {
        if (modifiers.focused) ref.current?.focus()
    }, [modifiers.focused])

    return (
        <Button
            ref={ref}
            variant="ghost"
            size="icon"
            rounded="lg"
            data-range-end={modifiers.range_end}
            data-day={day.date.toLocaleDateString()}
            data-range-start={modifiers.range_start}
            data-range-middle={modifiers.range_middle}
            data-selected-single={
                modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
            }
            className={cn(
                'tw:data-[selected-single=true]:bg-blue-500 tw:data-[selected-single=true]:text-primary-foreground',
                'tw:data-[range-middle=true]:bg-[#102855] tw:data-[range-middle=true]:text-accent-foreground',
                'tw:data-[range-start=true]:bg-blue-500 tw:data-[range-start=true]:text-primary-foreground',
                'tw:data-[range-end=true]:bg-blue-500 tw:data-[range-end=true]:text-primary-foreground',
                'tw:group-data-[focused=true]/day:border-ring tw:group-data-[focused=true]/day:ring-ring/50',
                'tw:dark:hover:text-accent-foreground tw:flex tw:aspect-square tw:size-auto tw:w-full tw:min-w-(--cell-size)',
                'tw:flex-col tw:gap-1 tw:leading-none tw:font-normal tw:group-data-[focused=true]/day:relative',
                'tw:group-data-[focused=true]/day:z-10 tw:group-data-[focused=true]/day:ring-[3px] tw:data-[range-end=true]:!rounded-lg',
                'tw:data-[range-end=true]:rounded-r-lg tw:data-[range-end=true]:!bg-blue-300 tw:data-[range-middle=true]:rounded-none tw:data-[range-start=true]:!rounded-lg',
                'tw:data-[range-start=true]:rounded-l-lg tw:data-[range-start=true]:!bg-blue-300 tw:[&>span]:text-xs tw:[&>span]:opacity-70 tw:!transition-none',
                'tw:dark:hover:border-1 tw:dark:hover:bg-[#102855] tw:dark:hover:border-blue-500 tw:dark:hover:!rounded-lg tw:outline-none',
                defaultClassNames.day,
                className,
            )}
            {...props}
        />
    )
}

export { Calendar, CalendarDayButton }
